import * as React from 'react';

import {ErrorState} from 'shells/error-state/error-state';

import {createPeanutScript} from '../../../../util/peanut-script';

import styles from './peanut-form-preview.module.css';

type Props = {
    // Form to preview
    publicFormId: string;
    // Instance id for error reporting
    instanceId: string;
    // True if getting preview of unpublished changes
    getUnpublishedVersion?: boolean;
    // True if preview fields should be disabled
    isNoTouchPreview?: boolean;
    // True if preview should be a thumbnail
    isThumbnail?: boolean;
};

export function PeanutFormPreview(props: Props) {
    const {publicFormId, instanceId, getUnpublishedVersion, isNoTouchPreview, isThumbnail} = props;

    const [formConfigJSON, setFormConfigJSON] = React.useState<string | null | undefined>();
    const [errorFetching, setErrorFetching] = React.useState<boolean>(false);

    const scriptRef = React.createRef();

    const shouldShowPreviewBar = getUnpublishedVersion && !isThumbnail;

    // Peanut needs the target to render the form preview there
    const target = `preview-form-${publicFormId}`;

    // On mount, get the form config using instance ID and form id
    React.useEffect(() => {
        getFormConfigJSON(
            NutshellFrontendSettings.instanceId,
            publicFormId,
            getUnpublishedVersion,
            Boolean(props.isThumbnail)
        )
            .then((response) => {
                if (response && response.config) {
                    setFormConfigJSON(JSON.stringify(response));
                } else {
                    throw new Error('Invalid response received from server');
                }
            })
            .catch((e) => {
                setErrorFetching(true);

                handlePeanutError(
                    instanceId,
                    publicFormId,
                    `${
                        getUnpublishedVersion ? 'Unpublished' : 'Published'
                    } form config could not be fetched`,
                    e.stack
                );
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (formConfigJSON) {
            const script = createPeanutScript(publicFormId);
            script.setAttribute('form-id', publicFormId);
            script.setAttribute('instance-id', instanceId);
            script.setAttribute('target', target);
            script.setAttribute('form-config', formConfigJSON);
            if (isNoTouchPreview) {
                script.setAttribute('no-touch', 'true');
            }
            if (isThumbnail) {
                script.setAttribute('is-thumbnail', 'true');
            }

            if (scriptRef && scriptRef.current) {
                // @ts-expect-error
                scriptRef.current.appendChild(script);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formConfigJSON]);

    React.useEffect(() => {
        if (!shouldShowPreviewBar) {
            return;
        }

        const bodyBackgroundColor = getFormBackgroundColorFromConfigJSON(
            JSON.parse(formConfigJSON || '{}')
        );

        if (bodyBackgroundColor && document.body) {
            document.body.style.backgroundColor = bodyBackgroundColor;
        }
    }, [formConfigJSON, shouldShowPreviewBar]);

    return (
        <div className={styles['peanut-preview-container']}>
            {errorFetching ? (
                <div className={styles['error-state']}>
                    <ErrorState />
                </div>
            ) : undefined}
            <div
                id={target}
                className={
                    props.isThumbnail ? styles['peanut-form--thumbnail'] : styles['peanut-form']
                }
            />
            {/* @ts-expect-error */}
            <div ref={scriptRef} />
        </div>
    );
}

// Fetch the form config JSON
function getFormConfigJSON(
    instanceId: string,
    publicFormId: string,
    getUnpublishedVersion: boolean | null | undefined,
    isThumbnail: boolean
): Promise<any> {
    let requestPath = `/webForms/config/${instanceId}/${publicFormId}?preview=true`;

    if (getUnpublishedVersion) {
        requestPath = requestPath.concat('&editable=true');
    } else if (isThumbnail) {
        requestPath = requestPath.concat('&thumbnail=true');
    }

    return fetch(requestPath)
        .then((response) => response.json())
        .then((data) => data);
}

// Report Peanut error to endpoint
function handlePeanutError(
    instanceId: string,
    publicFormId: string,
    errorDescription: string,
    errorStack: any
) {
    const requestOptions = {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
            errorDescription,
            errorStack,
            errorOrigin: window.location.href.replace(window.location.search, ''),
            isPreview: true,
        }),
    } as const;

    fetch(`/webForms/peanut-error/${instanceId}/${publicFormId}`, requestOptions).then(() => {
        // eslint-disable-next-line no-console
        console.error(`Logged error: [PEANUT PREVIEW]`, errorDescription);
    });
}

function getFormBackgroundColorFromConfigJSON(configJSON: any): string {
    // It's safer this way
    if (
        configJSON.config &&
        configJSON.config.formStyles &&
        configJSON.config.formStyles.backgroundColor
    ) {
        return configJSON.config.formStyles.backgroundColor;
    }

    // Just return white as a default
    return '#ffffff';
}
